import { CKB } from '@did/constants/chain'
import { Button } from '@did/das-balance-components'
import errno from '@did/constants/errno'
import { useConfigVal, useDasBalanceContext } from '@did/das-app-context'
import { ActivateInfoType } from '@did/das-balance-types'
import { thousandSplit } from '@did/tools'
import { ORDER_ACTION_TYPE } from '@did/types'
import React, { useState } from 'react'
import { Iconfont } from '@did/uikit'

export const ActivateInfo: ActivateInfoType = ({
  className,
  transitBalance,
  successCallback
}) => {
  const { lang, tt, services, connectedAccount, walletSdk, isProd, alert } =
    useDasBalanceContext()

  const [onTransferLoading, setOnTransferLoading] = useState(false)
  const config = useConfigVal({ services })
  const onActivate = async () => {
    if (!connectedAccount) return
    const { signTxList, onFailed } = await walletSdk?.initSignContext()

    try {
      setOnTransferLoading(true)
      const res = await services.account.transitBalanceTransfer({
        key_info: {
          coin_type: connectedAccount.chain?.coinType,
          key: connectedAccount.address
        },
        transfer_address: ''
      })

      if (!res) {
        return
      }

      const signatureList = await signTxList(res)

      const { hash } = await services.account.sendTrx(signatureList)
      successCallback?.({
        txHash: hash,
        txHashLink: `${CKB.getExplorerTrx(isProd)}${hash}`,
        actionType: ORDER_ACTION_TYPE.TRANSFER_BALANCE
      })
    } catch (err: any) {
      onFailed?.()
      console.log(err)
      if (
        ![
          errno.metaMaskUserRejectedAccountAccess,
          errno.metaMaskUserDeniedMessageSignature
        ].includes(err.code) &&
        err !== errno.tronLinkConfirmationDeclinedByUser &&
        err.message !== errno.walletConnectUserRejectedTheTransaction
      ) {
        if (
          err.code === errno.metaMaskWalletRequestPermissions ||
          err.code === errno.tronLinkAuthorizationRequestsAreBeingProcessed
        ) {
          alert({
            title: tt('Tips'),
            message: tt(
              'Other requests for the wallet are not processed, please try again after processing'
            )
          })
        } else if (err.message === errno.coinbaseWalletUsingMultipleWallet) {
          alert({
            title: tt('Tips'),
            message: tt(
              'Please check if you are using multiple wallet plugins. Please disable multiple wallet plugins, keep only one wallet plugin and try again.'
            )
          })
        } else if (err.code === errno.apiErrorCodeInsufficientBalance) {
          alert({
            title: tt('Fail to activate'),
            message: tt(
              'The amount to be activated needs to be greater than 350 CKB. Please click "Receive" to get your CKB address, transfer more CKB and try again.'
            )
          })
        } else if (err.code === errno.apiErrorCodeNotEnoughChange) {
          alert({
            title: tt('Tips'),
            message: tt(
              'DAS is a smart contract that runs on the Nervos. Due to the underlying logic of the contract, the remaining amount is too low (less than {minChangeCapacity} CKB) to send a transaction. ',
              {
                minChangeCapacity: config.min_change_capacity
              }
            )
          })
        } else if (err.code === errno.apiErrorCodeRejectedOutPoint) {
          alert({
            title: tt('Tips'),
            message: tt(
              'Frequent operations. There are still transactions being processed in your wallet address, please try again after 30s.'
            )
          })
        } else {
          alert({
            title: tt('Error'),
            message: err.code ? `${err.code}: ${err.message}` : `${err}`
          })
        }
      }
    } finally {
      setOnTransferLoading(false)
    }
  }

  return (
    <div
      className={` flex justify-between items-center rounded-xl bg-red-100  text-sm text-red-600 font-bold   ${className} `}
    >
      <span className=" flex items-center mr-8  ">
        <span>
          {tt('To be activated: {balance} CKB', {
            balance: thousandSplit(transitBalance)
          })}
        </span>
        <a
          className=" ml-1 text-center "
          href={
            lang?.value === 'zh-CN'
              ? 'https://talk.did.id/t/bestdas-com/115#ckb-8'
              : `https://talk.did.id/t/faq-bestdas-com/116#why-does-the-ckb-i-sent-needs-to-be-activated-and-is-the-activation-process-secure-8`
          }
          target="_blank"
        >
          <Iconfont
            name="help"
            size="14"
            color="#929BB1"
            style={{ verticalAlign: '-2px' }}
          />
        </a>
      </span>
      <Button small defaultLoading={onTransferLoading} onClick={onActivate}>
        {tt('Activate')}
      </Button>
    </div>
  )
}
